<script setup>
// Auto-extracted chrome for Waermepreis.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Wärmepreis" },
    brandTagline: { type: String, default: "" },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Wärmepreis");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap";
</script>

<template>
    <div class="wpr-root" :style="`--wpr-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="wpr-header">
                    <div class="wpr-header-inner">
                        <a href="/" class="wpr-brand">
                            <span class="wpr-brand-mark"></span>
                            <span class="wpr-brand-name">{{ brandName }}</span>
                        </a>
                        <span class="wpr-brand-tag">{{ brandTagline }}</span>
                    </div>
                </header>
        <slot />
        <nav class="wpr-legal" aria-label="Rechtliche Hinweise">
                    <a href="/impressum">Impressum</a>
                    <span aria-hidden="true">·</span>
                    <a href="/datenschutz">Datenschutz</a>
                    <span aria-hidden="true">·</span>
                    <a href="/agb">AGB</a>
                    <span aria-hidden="true">·</span>
                    <a href="/widerruf">Widerruf</a>
                    <a href="/ratgeber">Ratgeber</a>
                </nav>
        <footer class="wpr-footer">
                    <div class="wpr-footer-inner">
                        <span>{{ brandName }} - {{ brandTagline }}</span>
                        <span class="wpr-footer-meta">Orientierungswerte. Keine Preisgarantie.</span>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

.wpr-root {
    font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
    color: #1c1917;
    background: #fafaf9;
    min-height: 100vh;
}

.wpr-root :deep(*) { box-sizing: border-box; }

/* ── Header ───────────────────────────────────────────────────────────── */
.wpr-header {
    border-bottom: 1px solid #e7e5e4;
    background: #fff;
}
.wpr-header-inner {
    max-width: 1120px;
    margin: 0 auto;
    padding: 18px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
}
.wpr-brand {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    color: #1c1917;
}
.wpr-brand-mark {
    width: 28px;
    height: 28px;
    background: #1c1917;
    border-radius: 6px;
    position: relative;
}
.wpr-brand-mark::after {
    content: '';
    position: absolute;
    inset: 6px;
    background: #EA580C;
    border-radius: 2px;
}
.wpr-brand-name {
    font-family: 'Space Grotesk', 'Inter', sans-serif;
    font-weight: 700;
    font-size: 19px;
    letter-spacing: -0.01em;
}
.wpr-brand-tag {
    font-size: 13px;
    color: #57534e;
}

/* ── Hero ─────────────────────────────────────────────────────────────── */
.wpr-hero {
    background: #fff;
    border-bottom: 1px solid #e7e5e4;
}
.wpr-hero-inner {
    max-width: 1120px;
    margin: 0 auto;
    padding: 72px 24px 56px;
}
.wpr-eyebrow {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.18em;
    color: #78716c;
    margin: 0 0 20px;
}
.wpr-h1 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(34px, 5.8vw, 64px);
    line-height: 1.04;
    letter-spacing: -0.025em;
    font-weight: 700;
    margin: 0 0 24px;
    color: #1c1917;
}
.wpr-h1-accent {
    color: #EA580C;
}
.wpr-lede {
    font-size: 18.5px;
    line-height: 1.55;
    color: #44403c;
    max-width: 680px;
    margin: 0 0 40px;
}

.wpr-trust-strip {
    display: flex;
    align-items: center;
    gap: 0;
    background: #fafaf9;
    border: 1px solid #e7e5e4;
    border-radius: 10px;
    padding: 24px 0;
    max-width: 760px;
}
.wpr-trust-cell {
    flex: 1;
    text-align: center;
    padding: 0 24px;
}
.wpr-trust-num {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1c1917;
    letter-spacing: -0.02em;
}
.wpr-trust-lbl {
    font-size: 12.5px;
    color: #57534e;
    margin-top: 4px;
    line-height: 1.35;
}
.wpr-trust-sep {
    width: 1px;
    height: 44px;
    background: #e7e5e4;
}

/* ── Tool ─────────────────────────────────────────────────────────────── */
.wpr-tool {
    padding: 56px 24px 72px;
}
.wpr-tool-inner {
    max-width: 760px;
    margin: 0 auto;
}

.wpr-steps {
    list-style: none;
    padding: 0;
    margin: 0 0 28px;
    display: flex;
    gap: 8px;
    font-size: 13px;
    color: #a8a29e;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 500;
}
.wpr-steps li {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}
.wpr-steps li + li::before {
    content: '';
    width: 24px;
    height: 1px;
    background: #e7e5e4;
    margin-right: 8px;
}
.wpr-step-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #f5f5f4;
    color: #a8a29e;
    font-size: 12px;
    font-weight: 700;
}
.wpr-step-active { color: #1c1917; }
.wpr-step-active .wpr-step-num { background: #1c1917; color: #fff; }
.wpr-step-done { color: #57534e; }
.wpr-step-done .wpr-step-num { background: #EA580C; color: #fff; }

.wpr-card {
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 14px;
    padding: 36px 36px 32px;
}

.wpr-h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.015em;
    margin: 0 0 8px;
}
.wpr-help {
    font-size: 14.5px;
    color: #57534e;
    margin: 0 0 26px;
}

.wpr-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
    margin-bottom: 26px;
}
.wpr-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.wpr-label {
    font-size: 12.5px;
    font-weight: 600;
    color: #44403c;
    letter-spacing: 0.01em;
}
.wpr-input {
    appearance: none;
    border: 1px solid #d6d3d1;
    border-radius: 8px;
    background: #fff;
    padding: 12px 14px;
    font-family: inherit;
    font-size: 15px;
    color: #1c1917;
    transition: border-color 120ms ease, box-shadow 120ms ease;
}
.wpr-input:focus {
    outline: none;
    border-color: #1c1917;
    box-shadow: 0 0 0 3px rgba(28, 25, 23, 0.08);
}
.wpr-hint {
    font-size: 12px;
    color: #78716c;
}

.wpr-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #EA580C;
    color: #fff;
    border: 0;
    border-radius: 8px;
    padding: 16px 24px;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600;
    font-size: 15.5px;
    letter-spacing: 0.005em;
    cursor: pointer;
    transition: background 120ms ease, transform 120ms ease;
    width: 100%;
}
.wpr-cta:hover:not(:disabled) {
    background: #c2410c;
}
.wpr-cta:disabled {
    background: #d6d3d1;
    cursor: not-allowed;
}
.wpr-cta-secondary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: #1c1917;
    border: 1px solid #d6d3d1;
    border-radius: 8px;
    padding: 14px 22px;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600;
    font-size: 14.5px;
    cursor: pointer;
    transition: border-color 120ms ease, background 120ms ease;
    width: 100%;
    margin-top: 10px;
}
.wpr-cta-secondary:hover { border-color: #1c1917; background: #fafaf9; }

.wpr-fineprint {
    margin: 18px 0 0;
    font-size: 12px;
    color: #78716c;
    line-height: 1.5;
}

.wpr-back {
    background: none;
    border: 0;
    color: #57534e;
    font-size: 13.5px;
    cursor: pointer;
    padding: 0;
    margin-bottom: 18px;
}
.wpr-back:hover { color: #1c1917; }

.wpr-summary {
    background: #fafaf9;
    border: 1px solid #e7e5e4;
    border-radius: 8px;
    padding: 16px 20px;
    margin: 0 0 24px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 24px;
}
.wpr-summary > div {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
}
.wpr-summary span { color: #78716c; }
.wpr-summary strong { color: #1c1917; font-weight: 600; }

.wpr-consent {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    font-size: 12.5px;
    color: #57534e;
    line-height: 1.5;
    margin: 16px 0 22px;
}
.wpr-consent input {
    margin-top: 3px;
    flex-shrink: 0;
}
.wpr-error {
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 13.5px;
    margin: 0 0 14px;
}

/* ── Result ──────────────────────────────────────────────────────────── */
.wpr-eyebrow-result { color: #EA580C; margin-bottom: 16px; }

.wpr-median-block {
    text-align: center;
    padding: 24px 0 28px;
    border-bottom: 1px solid #e7e5e4;
    margin-bottom: 30px;
}
.wpr-median-label {
    font-size: 13px;
    color: #57534e;
    margin-bottom: 8px;
}
.wpr-median-value {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(48px, 8vw, 76px);
    line-height: 1;
    font-weight: 700;
    color: #1c1917;
    letter-spacing: -0.03em;
}
.wpr-median-sub {
    font-size: 13.5px;
    color: #57534e;
    margin-top: 12px;
}

.wpr-chart-wrap {
    margin-bottom: 28px;
}
.wpr-chart {
    width: 100%;
    height: 80px;
    display: block;
}
.wpr-chart-labels {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #78716c;
    margin-top: 6px;
    font-family: 'Space Grotesk', sans-serif;
}
.wpr-chart-med {
    font-weight: 600;
    color: #1c1917;
}

.wpr-delta-block {
    background: #fafaf9;
    border: 1px solid #e7e5e4;
    border-radius: 10px;
    padding: 20px 24px;
    margin-bottom: 28px;
}
.wpr-delta-label {
    font-size: 12.5px;
    color: #78716c;
    margin-bottom: 8px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-weight: 600;
}
.wpr-delta-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
}
.wpr-delta-amount {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1c1917;
}
.wpr-delta-pct {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 22px;
    font-weight: 700;
    padding: 4px 12px;
    border-radius: 6px;
}
.wpr-delta-good { color: #166534; background: #dcfce7; }
.wpr-delta-fair { color: #44403c; background: #f5f5f4; }
.wpr-delta-bad  { color: #991b1b; background: #fee2e2; }
.wpr-delta-text {
    font-size: 14px;
    color: #44403c;
    margin: 12px 0 0;
}

.wpr-result-cta { margin-top: 12px; }

.wpr-byline {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 28px;
    padding: 16px 0 0;
    border-top: 1px solid #e7e5e4;
}
.wpr-byline-avatar img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
}
.wpr-byline-name {
    font-weight: 600;
    font-size: 14px;
    color: #1c1917;
}
.wpr-byline-role {
    font-size: 12.5px;
    color: #78716c;
}

/* ── Methodik ─────────────────────────────────────────────────────────── */
.wpr-method {
    background: #1c1917;
    color: #fafaf9;
    padding: 64px 24px;
}
.wpr-method-inner {
    max-width: 760px;
    margin: 0 auto;
}
.wpr-method .wpr-h2 { color: #fff; margin-bottom: 24px; }
.wpr-method-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 18px;
}
.wpr-method-list li {
    padding: 18px 20px;
    background: rgba(255,255,255,0.04);
    border-left: 3px solid #EA580C;
    border-radius: 4px;
    font-size: 14.5px;
    line-height: 1.55;
    color: #d6d3d1;
}
.wpr-method-list strong { color: #fff; font-weight: 600; }

/* ── Footer ───────────────────────────────────────────────────────────── */
.wpr-footer {
    background: #fff;
    border-top: 1px solid #e7e5e4;
    padding: 24px;
}
.wpr-footer-inner {
    max-width: 1120px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: #78716c;
}
.wpr-footer-meta { color: #a8a29e; }

/* ── Sticky mobile ───────────────────────────────────────────────────── */
.wpr-sticky-mobile {
    display: none;
}

@media (max-width: 720px) {
    .wpr-hero-inner { padding: 48px 20px 40px; }
    .wpr-tool { padding: 40px 16px 96px; }
    .wpr-card { padding: 24px 20px; border-radius: 10px; }
    .wpr-grid { grid-template-columns: 1fr; gap: 14px; }
    .wpr-summary { grid-template-columns: 1fr; }
    .wpr-trust-strip { flex-wrap: wrap; padding: 16px 0; }
    .wpr-trust-cell { padding: 8px 16px; flex-basis: 100%; }
    .wpr-trust-sep { display: none; }
    .wpr-header-inner { flex-direction: column; align-items: flex-start; gap: 6px; padding: 14px 20px; }
    .wpr-brand-tag { font-size: 12px; }
    .wpr-steps { font-size: 11.5px; flex-wrap: wrap; }
    .wpr-footer-inner { flex-direction: column; gap: 6px; text-align: center; }

    .wpr-sticky-mobile {
        display: block;
        position: fixed;
        bottom: 0; left: 0; right: 0;
        padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
        background: rgba(255,255,255,0.96);
        border-top: 1px solid #e7e5e4;
        backdrop-filter: blur(8px);
        z-index: 50;
    }
    .wpr-cta-sticky { padding: 14px 20px; font-size: 15px; }
}

/* ── Trust strip (images + cites + byline) — wpr-* unique treatment ── */
.wpr-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.wpr-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .wpr-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.wpr-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 1px solid rgba(20,17,13,0.08); box-shadow: inset 0 0 0 4px #14110D1f, 0 10px 30px -16px rgba(20,17,13,0.22);
}
.wpr-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.wpr-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.wpr-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.wpr-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.wpr-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.wpr-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #14110D55;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.wpr-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.wpr-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #14110D;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.wpr-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.wpr-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.wpr-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.wpr-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.wpr-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.wpr-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


.wpr-legal{margin:48px auto 24px;text-align:center;font-size:13px;color:#807870}.wpr-legal a{color:inherit;text-decoration:none;margin:0 8px}.wpr-legal a:hover{color:#1c1917;text-decoration:underline}

</style>
